/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useCallback, useEffect, useState } from 'react';
import BeatLoader from 'react-spinners/BeatLoader';
import ModalAddStudent from '../../components/template/ModalAddStudent';
import ModalImportStudents from '../../components/template/ModalImportStudents';
import NavTop from '../../components/template/NavTop';
import {
  Body,
  Main,
  MainContent,
} from '../../components/template/SellerTemplate/styles';
import {
  AddButton,
  OptionLeft,
  OptionRight,
  Options,
  Title,
  TableContent,
} from '../../components/template/TemplateManager/styles';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { getStudents, getSellerIdByContentManagerUserId } from '../../services/api';
import { getLmsKey as getStoredLmsKey } from '../../services/auth';
import { IconAddCircle, IconDownload, IconUsersAlt } from '../../styles/icons';
import { Container, Content, Holder } from './styles';

interface IStudent {
  id: string;
  name: string;
  email: string;
  course: string;
}

const Students: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [students, setStudents] = useState<IStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalAddOpen, setModalAddOpen] = useState(false);
  const [modalImportOpen, setModalImportOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const getLmsKey = useCallback(async (): Promise<string | null> => {
    let lmsKey = getStoredLmsKey();
    if (!lmsKey && user?.id) {
      try {
        const { data } = await getSellerIdByContentManagerUserId(user.id);
        lmsKey = data.lms_key || null;
      } catch {
        // ignore
      }
    }
    return lmsKey;
  }, [user?.id]);

  const loadStudents = useCallback(async () => {
    const lmsKey = await getLmsKey();
    if (!lmsKey) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await getStudents(lmsKey);
      setStudents(Array.isArray(data) ? data : []);
    } catch (error) {
      addToast({
        title: 'Ops...',
        type: 'error',
        description: 'Não foi possível carregar os alunos.',
      });
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [getLmsKey, addToast]);

  useEffect(() => {
    if (user?.type === 'seller') {
      loadStudents();
    }
  }, [user?.type, refresh, loadStudents]);

  const clearFilter = useCallback(() => {
    setRefresh(prev => !prev);
  }, []);

  const toggleModalAdd = useCallback(() => {
    setModalAddOpen(prev => !prev);
  }, []);

  const toggleModalImport = useCallback(() => {
    setModalImportOpen(prev => !prev);
  }, []);

  const [lmsKey, setLmsKeyState] = useState<string | null>(null);
  useEffect(() => {
    getLmsKey().then(setLmsKeyState);
  }, [getLmsKey]);

  return (
    <Holder>
      <Main>
        <MainContent className="blur">
          <NavTop />
          <Body className="body">
            <ModalAddStudent
              isOpen={modalAddOpen}
              setIsOpen={toggleModalAdd}
              clearFilter={clearFilter}
              lmsKey={lmsKey}
            />
            <ModalImportStudents
              isOpen={modalImportOpen}
              setIsOpen={toggleModalImport}
              clearFilter={clearFilter}
              lmsKey={lmsKey}
            />
            <Container>
              <Options>
                <OptionRight>
                  <Title>
                    <h1>Alunos</h1>
                  </Title>
                </OptionRight>
                <OptionLeft style={{ flexDirection: 'row-reverse' }}>
                  <AddButton onClick={toggleModalImport}>
                    <IconDownload /> Importar CSV
                  </AddButton>
                  <AddButton onClick={toggleModalAdd}>
                    <IconAddCircle /> Novo Aluno
                  </AddButton>
                </OptionLeft>
              </Options>
              <Content>
                <TableContent>
                  {loading ? (
                    <div className="loading">
                      <BeatLoader size={24} />
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr className="no-cursor no-hover">
                          <th>Nome</th>
                          <th>Identificador de acesso</th>
                          <th>Curso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.length === 0 ? (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', padding: '24px' }}>
                              <IconUsersAlt style={{ opacity: 0.4, marginBottom: 8 }} />
                              <br />
                              Nenhum aluno cadastrado. Cadastre manualmente ou importe um CSV.
                            </td>
                          </tr>
                        ) : (
                          students.map(s => (
                            <tr key={s.id}>
                              <td>{s.name}</td>
                              <td>{s.email}</td>
                              <td>{s.course || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </TableContent>
              </Content>
            </Container>
          </Body>
        </MainContent>
      </Main>
    </Holder>
  );
};

export default Students;
